import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Chapter } from "../components/Chapter";
import { Reveal, MaskedLine, EASE } from "../lib/motion";
import { usePageSeo } from "../lib/i18nContext";
import LocalizedLink from "../components/LocalizedLink";
import { SequenceLines } from "../components/Motifs";

const WRAP = "max-w-[1240px] mx-auto px-6 md:px-12 xl:px-16";

const HowWeWork = () => {
  usePageSeo("how");
  const { t } = useTranslation();
  const steps = t("steps.items", { returnObjects: true });
  const concepts = t("howPage.s2.concepts", { returnObjects: true });

  return (
    <main className="pt-nav">
      <section data-testid="how-hero" className="on-navy bg-ink text-white relative overflow-hidden">
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-10 py-16 md:py-24`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs md:text-[13px] font-medium tracking-[0.18em] uppercase text-ice mb-8">{t("howPage.eyebrow")}</p>
            <h1 className="font-display font-semibold text-[36px] md:text-[44px] tracking-tight leading-[1.12] max-w-4xl" data-testid="how-headline">
              <MaskedLine delay={0.1}>{t("howPage.line1")}</MaskedLine>
              <MaskedLine delay={0.2}>{t("howPage.line2")}</MaskedLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.6, ease: EASE }} className="text-ice leading-relaxed mt-8 max-w-2xl text-base md:text-lg">
              {t("howPage.sub")}
            </motion.p>
          </div>
          <div className="lg:col-span-4 hidden lg:flex items-center" aria-hidden="true">
            <SequenceLines tone="dark" rows={10} gap={20} className="w-full h-[200px]" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-white">
        <div className={WRAP}>
          <Chapter index="01" label={t("howPage.s1chapter")} />
          <div className="mt-14 border-t border-ice">
            {steps.map((s, i) => (
              <Reveal key={s.title}>
                <div className="grid grid-cols-12 gap-6 py-10 md:py-14 border-b border-ice" data-testid={`process-step-0${i + 1}`}>
                  <div className="col-span-12 md:col-span-2">
                    <p className="font-mono text-2xl md:text-3xl font-medium text-pine">0{i + 1}</p>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <h2 className="font-display text-[22px] md:text-[26px] font-semibold text-ink leading-snug">{s.title}</h2>
                  </div>
                  <div className="col-span-12 md:col-span-6 flex items-center">
                    <p className="text-slate leading-relaxed md:text-lg">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-ice">
        <div className={WRAP}>
          <div className="max-w-3xl">
            <Chapter index="02" label={t("howPage.s2.chapter")} />
            <Reveal delay={0.05}>
              <h2 className="font-display text-[26px] md:text-[32px] font-semibold tracking-tight text-ink leading-tight mt-8">{t("howPage.s2.heading")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="flex flex-wrap gap-3 mt-10">
                {concepts.map((c) => (
                  <li key={c} className="border border-ink/15 bg-white px-4 py-2.5 rounded-sm font-mono text-xs text-ink">{c}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <LocalizedLink to="/contact" data-testid="how-cta" className="inline-flex items-center gap-2 mt-12 bg-ink text-white font-semibold px-8 py-4 rounded text-sm transition-colors duration-300 hover:bg-deep">
                {t("howPage.s2.cta")} <ArrowRight size={16} className="rtl:-scale-x-100" />
              </LocalizedLink>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HowWeWork;
